import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Tenant = {
  tenant_id: string;
  role: string;
};

type AuthState = {
  token: string | null;
  tenant: Tenant | null;
  apiBase: string;
  login: (apiKey: string) => Promise<void>;
  logout: () => Promise<void>;
  api: (path: string, init?: RequestInit) => Promise<Response>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({
  children,
  apiBase,
}: {
  children: React.ReactNode;
  apiBase: string;
}) {
  const [token, setToken] = useState<string | null>(
    () => (typeof localStorage !== 'undefined' ? localStorage.getItem('fdid_console_token') : null),
  );
  const [tenant, setTenant] = useState<Tenant | null>(
    () => {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem('fdid_console_tenant');
      return raw ? (JSON.parse(raw) as Tenant) : null;
    },
  );

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem('fdid_console_token', token);
    else localStorage.removeItem('fdid_console_token');
  }, [token]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (tenant) localStorage.setItem('fdid_console_tenant', JSON.stringify(tenant));
    else localStorage.removeItem('fdid_console_tenant');
  }, [tenant]);

  const login = async (apiKey: string) => {
    const res = await fetch(`${apiBase}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ api_key: apiKey }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(err.detail || 'Login failed');
    }
    const data = (await res.json()) as { token: string; tenant_id: string; role: string };
    setToken(data.token);
    setTenant({ tenant_id: data.tenant_id, role: data.role });
  };

  const logout = async () => {
    if (token) {
      await fetch(`${apiBase}/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => null);
    }
    setToken(null);
    setTenant(null);
  };

  const api = (path: string, init?: RequestInit) => {
    const headers = new Headers(init?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(`${apiBase}${path}`, { ...init, headers });
  };

  const value = useMemo(
    () => ({ token, tenant, apiBase, login, logout, api }),
    [token, tenant, apiBase],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
